// src/utils/carteleria/formularioCarteleria.js
//
// Lógica PURA del formulario de Cartelería (rediseño F4): parseo de montos, plazo derivado de las fechas,
// validación, armado del registro a guardar (con la coherencia que exigen los CHECK de la migración
// 20261006c) y resumen en vivo calculado con el motor `calendarioNegocio`. El componente `CarteleriaForm` solo
// pinta y llama a estas funciones (el proyecto no tiene @testing-library: lo testeable vive acá).
import { calendarioNegocio } from "./calendarioNegocio";

export const MODO_PRECIO = { MENSUAL: "mensual_pantalla", TOTAL: "total_contrato" };
export const FORMA_FACTURACION = { MENSUAL: "mensual", CADA_N: "cada_n_meses", UNICA: "unica" };
export const ESTADO_EDIFICIO_CADUCADO = "CADUCADO";

export const monedasDelPais = (pais) => (pais === "Chile" ? ["UF", "USD", "CLP"] : ["USD", "PEN"]);

/** Formato chileno: "." miles, "," decimal. Vacío o inválido → 0. */
export const parseMonto = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const num = parseFloat(String(value).replace(/\./g, "").replace(/,/g, "."));
  return Number.isNaN(num) ? 0 : num;
};

const pad2 = (n) => String(n).padStart(2, "0");
/** Date local → "YYYY-MM-DD" (sin corrimiento de huso). */
export const aYMD = (date) =>
  date ? `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}` : null;
/** "YYYY-MM-DD" → Date local (sin corrimiento de huso). */
export const desdeYMD = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).split("T")[0].split("-").map(Number);
  return new Date(y, m - 1, d);
};

/** Último día del mes de `fecha_inicio + plazo − 1` (el default legado de `fecha_fin`). */
export const finLegadoDe = (inicioYMD, plazoMeses) => {
  const plazo = parseInt(plazoMeses, 10) || 0;
  if (!inicioYMD || plazo < 1) return null;
  const [y, m] = inicioYMD.split("-").map(Number);
  const d = new Date(y, m - 1 + plazo, 0); // día 0 del mes siguiente al último de servicio
  return aYMD(d);
};

/** Meses (calendario, inclusive) entre el mes de inicio y el mes de fin — el `plazo_contrato_meses` legado. */
export const plazoDesdeFechas = (inicioYMD, finYMD) => {
  if (!inicioYMD || !finYMD) return null;
  const [y1, m1] = inicioYMD.split("-").map(Number);
  const [y2, m2] = finYMD.split("-").map(Number);
  const meses = (y2 - y1) * 12 + (m2 - m1) + 1;
  return meses >= 1 ? meses : null;
};

/** Edificios que se ofrecen: en altas no los CADUCADO; en edición/renovación se conserva el ya elegido. */
export const edificiosOfrecidos = (edificios, { esAlta, referenciaActual = null }) =>
  (edificios || []).filter(
    (e) =>
      !esAlta ||
      e.estado !== ESTADO_EDIFICIO_CADUCADO ||
      (referenciaActual != null && String(e.id) === String(referenciaActual))
  );

/**
 * Valida lo cargado. `v` = valores del formulario ya normalizados (ver `valoresDesdeFormulario` en el
 * componente). Devuelve `{ campo: mensaje }` (vacío = válido).
 */
export const validarCarteleria = (v, { esEdicion = false } = {}) => {
  const e = {};
  const esOT = v.tipo_vertical === "OT";
  if (!esOT) {
    if (!v.fecha_inicio) e.fecha_inicio = "Ingresá el inicio del servicio";
    if (!v.fecha_fin) e.fecha_fin = "Ingresá el fin del servicio";
    else if (v.fecha_inicio && v.fecha_fin < v.fecha_inicio) e.fecha_fin = "El fin no puede ser anterior al inicio";
    if (!(parseInt(v.cantidad_pantallas, 10) >= 1))
      e.cantidad_pantallas = "Ingresá la cantidad de pantallas (mínimo 1)";
    const total = v.modo_precio_gestion === MODO_PRECIO.TOTAL;
    if (!(parseMonto(total ? v.monto_total_gestion : v.fee_gestion_unitario) > 0)) {
      e.gestion_monto = total
        ? "El precio total del contrato debe ser mayor a 0"
        : "El precio por pantalla por mes debe ser mayor a 0";
    }
    if (!v.moneda_gestion) e.moneda_gestion = "Elegí la moneda de la gestión";
    if (v.forma_facturacion === FORMA_FACTURACION.CADA_N && !(parseInt(v.facturacion_cada_n_meses, 10) >= 2)) {
      e.facturacion_cada_n_meses = "Ingresá cada cuántos meses se factura (mínimo 2)";
    }
    if (v.incluye_contenidos) {
      if (parseMonto(v.fee_contenidos_unitario) > 0 && !v.moneda_contenidos)
        e.moneda_contenidos = "Elegí la moneda de los contenidos";
      if (v.contenidos_fecha_inicio && v.contenidos_fecha_fin && v.contenidos_fecha_fin < v.contenidos_fecha_inicio) {
        e.contenidos_fecha_fin = "El fin de contenidos no puede ser anterior al inicio";
      }
    }
  }
  if (v.incluye_hardware) {
    if (!v.moneda_hardware) e.moneda_hardware = "Elegí la moneda del hardware";
    if (v.modalidad === "CAPEX") {
      if (!(parseMonto(v.monto_hardware) > 0)) e.monto_hardware = "El monto del hardware debe ser mayor a 0";
      // Sin fecha de venta el hardware no aparece en ningún reporte (D9): obligatoria en altas.
      if (!v.fecha_cierre && !esEdicion) e.fecha_cierre = "Ingresá la fecha de venta del hardware";
    } else if (!(parseMonto(v.cuota_hardware_opex) > 0)) {
      e.cuota_hardware_opex = "La cuota mensual por pantalla debe ser mayor a 0";
    }
  }
  return e;
};

/**
 * Arma los campos de plata/fechas a guardar, con la coherencia que exigen los CHECK de la base:
 *  · `monto_total_gestion` solo con `total_contrato` (y > 0); `fee_gestion_unitario` = 0 en ese modo;
 *  · `facturacion_cada_n_meses` solo con `cada_n_meses` (≥ 2);
 *  · `plazo_contrato_meses` se DERIVA de las fechas (lo leen los consumidores legados).
 * Todo en el MISMO objeto (un solo insert/update).
 */
export const armarCamposCarteleria = (v) => {
  const esOT = v.tipo_vertical === "OT";
  const hardware = !!v.incluye_hardware;
  const base = {
    monto_hardware: hardware && v.modalidad === "CAPEX" ? parseMonto(v.monto_hardware) : 0,
    cuota_hardware_opex: hardware && v.modalidad === "OPEX" ? parseMonto(v.cuota_hardware_opex) : 0,
    moneda_hardware: hardware ? v.moneda_hardware : null,
    fecha_cierre: v.fecha_cierre || null,
  };
  if (esOT) {
    return {
      ...base,
      fee_gestion_unitario: null,
      moneda_gestion: null,
      incluye_contenidos: false,
      fee_contenidos_unitario: null,
      moneda_contenidos: null,
      contenidos_fecha_inicio: null,
      contenidos_fecha_fin: null,
      plazo_contrato_meses: null,
      cantidad_pantallas: null,
      fecha_fin: null,
      modo_precio_gestion: MODO_PRECIO.MENSUAL,
      monto_total_gestion: null,
      forma_facturacion: FORMA_FACTURACION.MENSUAL,
      facturacion_cada_n_meses: null,
      prorrata_por_dias: false,
    };
  }
  const total = v.modo_precio_gestion === MODO_PRECIO.TOTAL;
  const contenidos = !!v.incluye_contenidos;
  const cadaN = v.forma_facturacion === FORMA_FACTURACION.CADA_N;
  return {
    ...base,
    fee_gestion_unitario: total ? 0 : parseMonto(v.fee_gestion_unitario),
    moneda_gestion: v.moneda_gestion,
    modo_precio_gestion: total ? MODO_PRECIO.TOTAL : MODO_PRECIO.MENSUAL,
    monto_total_gestion: total ? parseMonto(v.monto_total_gestion) : null,
    incluye_contenidos: contenidos,
    fee_contenidos_unitario: contenidos ? parseMonto(v.fee_contenidos_unitario) : 0,
    moneda_contenidos: contenidos ? v.moneda_contenidos || null : null,
    contenidos_fecha_inicio: contenidos ? v.contenidos_fecha_inicio || null : null,
    contenidos_fecha_fin: contenidos ? v.contenidos_fecha_fin || null : null,
    fecha_inicio: v.fecha_inicio,
    fecha_fin: v.fecha_fin,
    plazo_contrato_meses: plazoDesdeFechas(v.fecha_inicio, v.fecha_fin),
    cantidad_pantallas: parseInt(v.cantidad_pantallas, 10) || null,
    forma_facturacion: v.forma_facturacion || FORMA_FACTURACION.MENSUAL,
    facturacion_cada_n_meses: cadaN ? parseInt(v.facturacion_cada_n_meses, 10) : null,
    prorrata_por_dias: !!v.prorrata_por_dias,
  };
};

/** Objeto con la forma de una fila de `negocios_carteleria`, para alimentar al motor desde el formulario. */
export const negocioDesdeValores = (v) => ({
  id: v.id || "nuevo",
  tipo_vertical: v.tipo_vertical,
  estado: v.estado || "Activo",
  pais: v.pais,
  ...armarCamposCarteleria(v),
  fecha_inicio: v.fecha_inicio || null,
  incluye_hardware: !!v.incluye_hardware,
  modalidad: v.incluye_hardware ? v.modalidad : null,
});

const r2 = (x) => Math.round(x * 100) / 100;

/**
 * Resumen en vivo del contrato que se está cargando, con el MISMO motor que lee el resto de la app.
 * `hoy` = día de inicio del servicio: es una vista de lo contratado, sin la prórroga de "el estado manda".
 * @returns {null | { totales, facturas: { cantidad, lista }, advertencias }}
 */
export const resumenEnVivo = (v) => {
  if (!v.fecha_inicio && v.tipo_vertical !== "OT") return null;
  const negocio = negocioDesdeValores(v);
  const hoy = negocio.fecha_inicio || v.fecha_cierre || "2026-01-01";
  const cal = calendarioNegocio(negocio, { hoy });
  const suma = (comp) => {
    const items = cal.devengo.filter((d) => d.componente === comp);
    return items.length > 0 ? { monto: r2(items.reduce((s, d) => s + d.monto, 0)), moneda: items[0].moneda } : null;
  };
  const totales = [];
  const g = suma("gestion");
  if (g) totales.push({ clave: "gestion", etiqueta: "Gestión", ...g });
  const c = suma("contenidos");
  if (c) totales.push({ clave: "contenidos", etiqueta: "Contenidos", ...c });
  const o = suma("opex");
  if (o) totales.push({ clave: "opex", etiqueta: "Hardware OPEX (cuotas)", ...o });
  if (cal.hardwareCapex && cal.hardwareCapex.monto > 0) {
    totales.push({
      clave: "hardware",
      etiqueta: "Hardware CAPEX",
      monto: cal.hardwareCapex.monto,
      moneda: cal.hardwareCapex.moneda,
    });
  }
  const facturas = {
    cantidad: cal.cobros.length,
    lista: cal.cobros.map((x) => ({
      mes: x.mes,
      tipo: x.tipo,
      etiqueta: x.etiqueta || null,
      totalesPorMoneda: x.totalesPorMoneda,
    })),
  };
  return { totales, facturas, advertencias: cal.advertencias, modo: cal.modo };
};

/** Texto de un monto con su moneda, p. ej. "US$ 2.160" o "UF 7,8" (formato chileno, sin depender de Intl). */
export const formatoMonto = (monto, moneda) => {
  const prefijo = { USD: "US$", PEN: "S/", CLP: "$", UF: "UF" }[moneda] || moneda || "";
  const decimales = moneda === "CLP" ? 0 : 2;
  const [entero, dec] = Math.abs(Number(monto)).toFixed(decimales).split(".");
  const miles = entero.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const decUtil = dec && Number(dec) !== 0 ? `,${dec.replace(/0$/, "")}` : "";
  return `${prefijo} ${Number(monto) < 0 ? "-" : ""}${miles}${decUtil}`.trim();
};
